// src/pages/admin/AdminModeration.js — Maxula
// Brique 4 · 4.2 — modération des tentatives de désintermédiation (chat).
import React, { useEffect, useState, useCallback } from 'react';
import { FiAlertTriangle, FiCheck, FiRefreshCw, FiShield, FiSlash } from 'react-icons/fi';
import { adminAPI } from '../../services/api';

const Card = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-red-50 text-red-500 flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900 leading-none">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  </div>
);

const AdminModeration = () => {
  const [summary, setSummary] = useState({ data: [], totals: { attempts: 0, conversations: 0, merchants: 0 } });
  const [flags, setFlags] = useState([]);
  const [status, setStatus] = useState('pending'); // pending | all
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const loadSummary = useCallback(async () => {
    try { const res = await adminAPI.moderationSummary(); setSummary(res.data || { data: [], totals: {} }); }
    catch (e) {}
  }, []);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.moderationFlags({ status, merchantId: merchantId || undefined, limit: 100 });
      setFlags(res.data?.data || []);
    } catch (e) { setFlags([]); }
    finally { setLoading(false); }
  }, [status, merchantId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadFlags(); }, [loadFlags]);

  const review = async (id) => {
    setBusy(id);
    try { await adminAPI.reviewModerationFlag(id, true); await Promise.all([loadFlags(), loadSummary()]); }
    catch (e) {} finally { setBusy(null); }
  };

  // Phase 4.5 — restriction / réinitialisation des récidivistes
  const [busyM, setBusyM] = useState(null);
  const toggleRestriction = async (m) => {
    setBusyM(m.merchantId);
    try { await adminAPI.setMerchantRestriction(m.merchantId, !m.restricted); await loadSummary(); }
    catch (e) {} finally { setBusyM(null); }
  };
  const resetStrikes = async (m) => {
    setBusyM(m.merchantId);
    try { await adminAPI.resetMerchantStrikes(m.merchantId); await loadSummary(); }
    catch (e) {} finally { setBusyM(null); }
  };

  const totals = summary.totals || {};

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiShield className="text-red-500" /> Modération — désintermédiation
          </h1>
          <p className="text-sm text-gray-500 mt-1 mb-5 max-w-3xl">
            Tentatives de partage de coordonnées dans le chat. Le contact est déjà masqué au
            stockage ; cet écran sert à repérer les récidivistes et à traiter les alertes.
          </p>
        </div>
        <button onClick={() => { loadFlags(); loadSummary(); }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm">
          <FiRefreshCw size={15} /> Rafraîchir
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Card label="Tentatives totales" value={totals.attempts || 0} icon={FiAlertTriangle} />
        <Card label="Conversations concernées" value={totals.conversations || 0} icon={FiAlertTriangle} />
        <Card label="Marchands concernés" value={totals.merchants || 0} icon={FiAlertTriangle} />
      </div>

      {/* Récidivistes par marchand */}
      {summary.data && summary.data.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Récidivistes (par marchand)</h2>
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Marchand</th>
                  <th className="px-4 py-2 text-left">Code</th>
                  <th className="px-4 py-2 text-right">Tentatives</th>
                  <th className="px-4 py-2 text-right">Avertissements</th>
                  <th className="px-4 py-2 text-center">Statut</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {summary.data.map((m) => (
                  <tr key={m.merchantId} className={m.restricted ? 'bg-red-50' : (m.attempts >= 3 ? 'bg-red-50/40' : '')}>
                    <td className="px-4 py-2 font-medium text-gray-800">{m.name}</td>
                    <td className="px-4 py-2 text-gray-500">{m.code || '—'}</td>
                    <td className="px-4 py-2 text-right font-semibold text-red-600">{m.attempts}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{m.strikes || 0}</td>
                    <td className="px-4 py-2 text-center">
                      {m.restricted
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"><FiSlash className="shrink-0" /> Restreint</span>
                        : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Actif</span>}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setMerchantId(String(m.merchantId))}
                        className="text-primary-600 hover:underline text-xs mr-3">Voir</button>
                      <button onClick={() => toggleRestriction(m)} disabled={busyM === m.merchantId}
                        className={`text-xs font-semibold mr-3 disabled:opacity-50 ${m.restricted ? 'text-green-600 hover:underline' : 'text-red-600 hover:underline'}`}>
                        {busyM === m.merchantId ? '…' : (m.restricted ? 'Lever' : 'Restreindre')}
                      </button>
                      <button onClick={() => resetStrikes(m)} disabled={busyM === m.merchantId}
                        className="text-xs font-semibold text-gray-500 hover:underline disabled:opacity-50">
                        Réinitialiser
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-2 mb-3">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          <button onClick={() => setStatus('pending')}
            className={`px-3 py-1.5 ${status === 'pending' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            À traiter
          </button>
          <button onClick={() => setStatus('all')}
            className={`px-3 py-1.5 ${status === 'all' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            Tout
          </button>
        </div>
        {merchantId && (
          <button onClick={() => setMerchantId('')}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
            Marchand #{merchantId} — retirer le filtre
          </button>
        )}
      </div>

      {/* Liste des tentatives */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-9 w-9 border-b-2 border-primary-600" /></div>
      ) : flags.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
          Aucune tentative {status === 'pending' ? 'à traiter' : ''}.
        </div>
      ) : (
        <div className="space-y-2">
          {flags.map((f) => (
            <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-1">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${f.senderRole === 'merchant' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                    {f.senderRole === 'merchant' ? 'Marchand' : 'Client'}
                  </span>
                  {f.merchant && <span>Marchand : <b className="text-gray-700">{f.merchant.name}</b></span>}
                  {f.customer && <span>· Client : {f.customer.name}</span>}
                  {f.dealTitle && <span>· « {f.dealTitle} »</span>}
                  <span>· {new Date(f.createdAt).toLocaleString('fr-FR')}</span>
                </div>
                <div className="text-sm text-gray-800 break-words">{f.text}</div>
              </div>
              {!f.reviewed ? (
                <button onClick={() => review(f.id)} disabled={busy === f.id}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-50">
                  <FiCheck size={14} /> {busy === f.id ? '...' : 'Traité'}
                </button>
              ) : (
                <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 text-xs">
                  <FiCheck size={14} /> Traité
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminModeration;
